import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import { ROUTES } from "../../../constants";
import { useAuth } from "../../auth/hooks/useAuth";
import { useClientProfile } from "../../auth/hooks/useClientProfile";
import { useOrganization } from "../../auth/hooks/useOrganization";
import { useExpandedProtocol } from "../hooks/useExpandedProtocol";
import { useExercisesMap } from "../hooks/useExercisesMap";
import { useTrainingPlanTheme } from "../hooks/useTrainingPlanTheme";
import { useCurrentWorkout } from "../../dashboard/hooks/useCurrentWorkout";
import WorkoutCard from "../components/WorkoutCard";
import "./training-plan-view.css";

export default function TrainingPlanPage() {
  const navigate = useNavigate();
  const { clientId, organizationId, isLoading: authLoading } = useAuth();
  const { client, loading: clientLoading } = useClientProfile();
  const { organization } = useOrganization(organizationId);
  const protocolId = client?.protocolId ?? null;

  useTrainingPlanTheme(organization?.brandingColors);

  const { protocol, loading: protocolLoading, error: protocolError } = useExpandedProtocol({
    organizationId,
    protocolId,
  });
  const { getExerciseName, findById, loading: exercisesLoading } = useExercisesMap(
    organizationId,
    !!protocolId
  );
  const { currentWorkout } = useCurrentWorkout({ clientId, organizationId });

  const currentSplitId = currentWorkout?.reportData?.split?.id ?? null;
  const scrollToRef = useRef(null);

  useEffect(() => {
    if (currentSplitId && protocol?.splitsExercises && scrollToRef.current) {
      scrollToRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentSplitId, protocol?.splitsExercises]);

  const waitingForProfile = authLoading || (clientId && clientLoading && !client);
  if (waitingForProfile) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading your training plan...</Typography>
        </Box>
      </Container>
    );
  }

  if (!protocolId) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No plan assigned
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your coach has not assigned a training plan yet.
          </Typography>
          <Button variant="contained" onClick={() => navigate(ROUTES.DASHBOARD)}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  const loading = protocolLoading || exercisesLoading;
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading your training plan...</Typography>
        </Box>
      </Container>
    );
  }

  if (protocolError) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 2 }}>
          {protocolError.message ?? "Failed to load training plan."}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(ROUTES.DASHBOARD)}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!protocol?.protocolData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No plan data available.</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate(ROUTES.DASHBOARD)}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  const protocolData = protocol.protocolData;
  const splitsExercises = protocol.splitsExercises ?? [];
  const lastUpdated = protocolData.updatedOn || protocolData.createdOn;
  const lastUpdatedStr = lastUpdated ? format(new Date(lastUpdated), "MMM d, yyyy") : null;
  const orgName = organization?.name || "Training Plan";

  return (
    <Container maxWidth="md" className="training-plan-page">
      <div className="training-plan-toolbar">
        <Button
          startIcon={<PrintIcon />}
          variant="outlined"
          size="small"
          onClick={() => navigate(ROUTES.PRINTABLE_PLAN)}
        >
          Printable Version
        </Button>
      </div>

      {currentWorkout ? (
        <div
          ref={currentSplitId ? scrollToRef : null}
          className="pp-current-workout"
        >
          <p className="pp-current-workout-label">Current workout</p>
          <p className="pp-current-workout-name">
            {currentWorkout?.reportData?.split?.name ?? "—"}
          </p>
          {currentWorkout?.reportData?.protocol?.name ? (
            <p className="pp-current-workout-sub">
              from {currentWorkout.reportData.protocol.name}
            </p>
          ) : null}
        </div>
      ) : null}

      <header className="pp-cover">
        <p className="pp-cover-org">{orgName}</p>
        <h1 className="pp-cover-title">{protocolData.name}</h1>
        {protocolData.description ? (
          <div className="pp-cover-description">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {protocolData.description}
            </ReactMarkdown>
          </div>
        ) : null}
        <div className="pp-cover-meta">
          <span>
            {splitsExercises.length} workout{splitsExercises.length !== 1 ? "s" : ""}
          </span>
          {lastUpdatedStr ? <span>Last updated {lastUpdatedStr}</span> : null}
        </div>
      </header>

      <h2 className="pp-section-heading">Training Schedule</h2>
      <p className="pp-section-intro">
        Tap a workout to expand exercises. Your current workout is highlighted above when
        available.
      </p>

      {splitsExercises.map((workout, index) => (
        <WorkoutCard
          key={workout.id ?? index}
          workout={workout}
          index={index}
          getExerciseName={getExerciseName}
          findById={findById}
          defaultExpanded={workout.id === currentSplitId}
        />
      ))}
    </Container>
  );
}
